import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { logger } from '../lib/logger';
import { queryCache } from '../lib/cache/queryCache';

interface Organization {
  id: string;
  name: string;
  slug: string;
  type: string;
  logo_url: string | null;
  primary_color: string;
  secondary_color: string;
  role: 'admin' | 'official' | 'citizen';
}

interface OrganizationContextType {
  organizations: Organization[];
  currentOrg: Organization | null;
  switchOrganization: (orgId: string) => void;
  loading: boolean;
  refreshOrganizations: () => Promise<Organization[]>;
  createOrganization: (name: string, slug: string, type: string) => Promise<Organization>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUserId, setLastUserId] = useState<string | null>(null);

  const fetchOrganizations = async (): Promise<Organization[]> => {
    if (!user) {
      logger.log('[OrganizationContext] No user, clearing organizations');
      setOrganizations([]);
      setCurrentOrg(null);
      // Only set loading to false if auth is also not loading
      // This prevents showing AuthPage while auth session is being restored
      if (!authLoading) {
        setLoading(false);
      }
      return [];
    }

    // When user exists, ensure loading is true while fetching
    setLoading(true);

    try {
      logger.log('[OrganizationContext] Fetching organizations for user');

      const { data, error } = await supabase
        .from('organization_members')
        .select(`
          role,
          organization:organizations(
            id,
            name,
            slug,
            type,
            logo_url,
            primary_color,
            secondary_color
          )
        `)
        .eq('user_id', user.id);

      if (error) throw error;

      const orgs: Organization[] = (data || []).map((item: any) => ({
        ...item.organization,
        type: item.organization?.type || 'Gobierno',
        primary_color: item.organization?.primary_color || '#5D1826',
        secondary_color: item.organization?.secondary_color || '#B89658',
        role: item.role
      }));

      logger.log('[OrganizationContext] Fetched organizations:', orgs.length);
      setOrganizations(orgs);

      // Set current org: use stored preference or first available
      const storedOrgId = localStorage.getItem('currentOrganizationId');
      const preferred = orgs.find((o: Organization) => o.id === storedOrgId);
      const selected = preferred || orgs[0] || null;

      logger.log('[OrganizationContext] Current organization set');
      setCurrentOrg(selected);

      // Return the organizations so createOrganization can use them
      return orgs;
    } catch (error) {
      logger.error('[OrganizationContext] Error fetching organizations', error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch organizations if user ID actually changed
    // This prevents redundant fetches during Android file picker operations
    const currentUserId = user?.id || null;

    if (currentUserId !== lastUserId) {
      logger.log('[OrganizationContext] User ID changed, fetching organizations');
      setLastUserId(currentUserId);
      fetchOrganizations();
    } else if (!authLoading && currentUserId === null && lastUserId === null) {
      // Initial load with no user
      fetchOrganizations();
    } else {
      logger.log('[OrganizationContext] User ID unchanged, skipping fetch');
    }
  }, [user, authLoading]);

  const switchOrganization = (orgId: string) => {
    const org = organizations.find(o => o.id === orgId);
    if (org) {
      logger.log('[OrganizationContext] Switching organization');

      // SCALE-HIGH-001 Fix: Clear all cached data when switching organizations
      // This ensures users don't see data from the previous organization
      queryCache.invalidate('');
      logger.log('[OrganizationContext] Cache cleared on organization switch');

      setCurrentOrg(org);
      localStorage.setItem('currentOrganizationId', orgId);
    }
  };

  const createOrganization = async (name: string, slug: string, type: string): Promise<Organization> => {
    if (!user) {
      throw new Error('Must be authenticated to create organization');
    }

    logger.log('[OrganizationContext] Creating organization');

    try {
      // Create organization using RPC function (bypasses RLS)
      const { data: orgData, error: orgError } = await supabase
        .rpc('create_organization_bypass_rls', {
          p_name: name,
          p_slug: slug,
          p_type: type
        });

      if (orgError) throw orgError;

      logger.log('[OrganizationContext] Organization created successfully');

      // The trigger will automatically add the creator as admin
      // Wait a moment for the trigger to complete
      await new Promise(resolve => setTimeout(resolve, 500));

      // Refresh organizations to get the new one with role
      const refreshedOrgs = await fetchOrganizations();

      // Find and return the new organization from the refreshed data
      const newOrg = refreshedOrgs.find((o: Organization) => o.id === orgData.id);
      if (!newOrg) {
        // If still not found, just return the basic org data
        // The user will be redirected to the main app anyway
        logger.warn('[OrganizationContext] Organization created but not found in members list yet');
        return {
          id: orgData.id,
          name: orgData.name,
          slug: orgData.slug,
          type: orgData.type || 'Gobierno',
          logo_url: orgData.logo_url || null,
          primary_color: orgData.primary_color || '#5D1826',
          secondary_color: orgData.secondary_color || '#B89658',
          role: 'admin' as const
        };
      }

      return newOrg;
    } catch (error) {
      logger.error('[OrganizationContext] Error creating organization', error);
      throw error;
    }
  };

  return (
    <OrganizationContext.Provider value={{
      organizations,
      currentOrg,
      switchOrganization,
      loading,
      refreshOrganizations: fetchOrganizations,
      createOrganization
    }}>
      {children}
    </OrganizationContext.Provider>
  );
}

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within OrganizationProvider');
  }
  return context;
};
